import { useEffect, useState } from "react";
import { apiJson, ApiError } from "../api";
import { LogOut, WalletCards, X } from "lucide-react";
import type {
  AccountUser,
  BillingProduct,
  CreditWallet,
  ServiceStatus
} from "../types";

type Props = {
  open: boolean;
  user: AccountUser | null;
  wallet: CreditWallet | null;
  products: BillingProduct[];
  serviceStatus: ServiceStatus | null;
  onClose: () => void;
  onWalletChange: (wallet: CreditWallet) => void;
  onLogout: () => void;
};

type Coupon = {
  id: string;
  code: string;
  name: string;
  discountType: string;
  discountValue: number;
  maxDiscountCents: number | null;
  minPurchaseCents: number;
  productScope: { all: boolean; codes?: string[] } | null;
  status: string;
  expiresAt: string;
};

function couponDiscountCents(coupon: Coupon, priceCents: number): number {
  let discount: number;
  if (coupon.discountType === "percent") {
    discount = Math.round((priceCents * coupon.discountValue) / 100);
    if (coupon.maxDiscountCents != null) {
      discount = Math.min(discount, coupon.maxDiscountCents);
    }
  } else {
    discount = coupon.discountValue;
  }
  return Math.min(discount, priceCents);
}

function couponAppliesTo(coupon: Coupon, product: BillingProduct): boolean {
  if (coupon.status !== "unused") return false;
  if (new Date(coupon.expiresAt).getTime() <= Date.now()) return false;
  if (product.priceCents < coupon.minPurchaseCents) return false;
  const scope = coupon.productScope;
  if (scope && !scope.all) {
    const codes = scope.codes || [];
    if (!codes.includes(product.code)) return false;
  }
  return true;
}

function bestCouponFor(
  coupons: Coupon[],
  product: BillingProduct
): Coupon | null {
  const applicable = coupons.filter((c) => couponAppliesTo(c, product));
  if (applicable.length === 0) return null;
  return applicable.reduce((best, c) =>
    couponDiscountCents(c, product.priceCents) >
    couponDiscountCents(best, product.priceCents)
      ? c
      : best
  );
}

export function BillingModal({
  open,
  user,
  wallet,
  products,
  serviceStatus,
  onClose,
  onWalletChange,
  onLogout
}: Props) {
  const [payingCode, setPayingCode] = useState<string | null>(null);
  const [payError, setPayError] = useState<string | null>(null);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [selectedCouponId, setSelectedCouponId] = useState<string | null>(null);
  const [couponTouched, setCouponTouched] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    setCoupons([]);
    setSelectedCouponId(null);
    setCouponTouched(false);
    apiJson<{ coupons: Coupon[] }>("/api/marketing/coupons")
      .then((result) => setCoupons(result.coupons || []))
      .catch(() => setCoupons([]));
  }, [open, user]);

  if (!open || !user) return null;

  const isWeChatBrowser = typeof window !== "undefined" && /MicroMessenger/i.test(window.navigator.userAgent || "");

  const usableCoupons = coupons.filter((c) =>
    products.some((p) => couponAppliesTo(c, p))
  );

  const couponForProduct = (product: BillingProduct): Coupon | null => {
    if (couponTouched) {
      const selected = coupons.find((c) => c.id === selectedCouponId) || null;
      return selected && couponAppliesTo(selected, product) ? selected : null;
    }
    return bestCouponFor(coupons, product);
  };

  const selectCoupon = (coupon: Coupon) => {
    setCouponTouched(true);
    setSelectedCouponId((prev) => (prev === coupon.id ? null : coupon.id));
  };

  const handleAlipay = async (productCode: string) => {
    if (payingCode) return;
    setPayError(null);
    setPayingCode(productCode);
    const product = products.find((p) => p.code === productCode);
    const coupon = product ? couponForProduct(product) : null;
    try {
      const res = await apiJson<{ paymentUrl: string; order: { id: string } }>(
        "/api/billing/orders/alipay",
        {
          method: "POST",
          body: JSON.stringify({
            productCode,
            couponId: coupon?.id ?? null,
          }),
        }
      );
      if (res.paymentUrl) {
        window.location.href = res.paymentUrl;
      } else {
        throw new Error("未获取到支付链接");
      }
    } catch (error) {
      const message = error instanceof ApiError ? error.message : String(error);
      setPayError(message || "支付发起失败");
      setPayingCode(null);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="billing-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="billing-modal-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="modal-header">
          <div>
            <span className="eyebrow">账户与额度</span>
            <h2 id="billing-modal-title">选择额度包</h2>
          </div>
          <button className="modal-close" type="button" onClick={onClose} aria-label="关闭">
            <X size={20} />
          </button>
        </header>

        <div className="account-summary">
          <div>
            <span>{user.email}</span>
            <strong><WalletCards size={17} /> 可用 {wallet?.balance ?? 0} 次</strong>
          </div>
          <button type="button" onClick={onLogout}>
            <LogOut size={15} />
            退出
          </button>
        </div>

        {payError && (
          <div className="billing-error" role="alert">
            {payError}
          </div>
        )}

        {usableCoupons.length > 0 && (
          <div className="coupon-section">
            <div className="coupon-section-title">
              <strong>我的优惠券</strong>
              <span>点击选择；再点一次取消使用</span>
            </div>
            <div className="coupon-scroll">
              {usableCoupons.map((coupon) => {
                const scope = coupon.productScope;
                const scopeText =
                  scope && !scope.all
                    ? products.find((p) => (scope.codes || []).includes(p.code))?.name || "指定套餐"
                    : "全部套餐";
                const selected = couponTouched
                  ? selectedCouponId === coupon.id
                  : products.some((p) => bestCouponFor(coupons, p)?.id === coupon.id);
                return (
                  <button
                    key={coupon.id}
                    type="button"
                    className={`coupon-card${selected ? " coupon-card--selected" : ""}`}
                    aria-pressed={selected}
                    onClick={() => selectCoupon(coupon)}
                  >
                    <span className="coupon-value">
                      {coupon.discountType === "percent"
                        ? `${coupon.discountValue} 折`
                        : `减 ¥${(coupon.discountValue / 100).toFixed(0)}`}
                    </span>
                    <span className="coupon-name">{coupon.name}</span>
                    <span className="coupon-limit">
                      限 {scopeText} · 满 ¥{(coupon.minPurchaseCents / 100).toFixed(0)} 可用
                    </span>
                    <span className="coupon-expiry">
                      有效至 {coupon.expiresAt.slice(0, 10)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="pricing-grid">
          {products.map((product) => {
            const coupon = couponForProduct(product);
            const discount = coupon
              ? couponDiscountCents(coupon, product.priceCents)
              : 0;
            const finalPrice = product.priceCents - discount;
            return (
              <article className="pricing-card" key={product.code}>
                <span>{product.name}</span>
                <strong>
                  ¥{(finalPrice / 100).toFixed(discount % 100 === 0 ? 0 : 2)}
                  {coupon && (
                    <small className="pricing-original">
                      ¥{(product.priceCents / 100).toFixed(0)}
                    </small>
                  )}
                </strong>
                <b>{product.credits} 次对话</b>
                <p>{product.description}</p>
                {coupon ? (
                  <small className="pricing-coupon">
                    已使用「{coupon.name}」抵扣 ¥{(discount / 100).toFixed(2)}
                  </small>
                ) : (
                  <small>约 ¥{(product.priceCents / 100 / product.credits).toFixed(2)} / 次</small>
                )}
                <button
                  type="button"
                  className="alipay-button"
                  disabled={Boolean(payingCode)}
                  onClick={() => void handleAlipay(product.code)}
                >
                  {payingCode === product.code ? "正在跳转支付宝…" : "支付宝购买"}
                </button>
              </article>
            );
          })}
        </div>

        {isWeChatBrowser && (
          <p className="billing-note billing-note--wechat">
            微信内无法直接完成支付宝支付，请点击右上角「···」选择「在浏览器打开」后再购买。
          </p>
        )}

        <p className="billing-note">每个用户问题成功获得有效模型回答后扣 1 次；无输出失败会自动退回。</p>
      </section>
    </div>
  );
}
